import React, { useEffect, useRef, useState } from "react";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Close,
  DeleteOutline,
  DragIndicator,
  LockOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  WORKFLOW_FIELD_DATA_TYPES,
  WorkflowFieldDataType,
  WorkflowFieldVisibility,
  WorkflowFormField,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

// Distinct drag types so an option dragged out of its card can never be read as a field drag, and
// so `dragover` can accept or reject a payload it isn't allowed to read yet (the DataTransfer is
// in protected mode until drop — only `types` is readable).
export const FIELD_DRAG_TYPE = "application/x-workflow-field";
const OPTION_DRAG_TYPE = "application/x-workflow-option";
const BLOCKED_FILE_TYPES = new Set(["exe", "bat", "ps1"]);
const TYPES_WITH_SETTINGS: WorkflowFieldDataType[] = ["text", "richtext", "number", "file"];

interface WorkflowFormFieldRowProps {
  field: WorkflowFormField;
  index: number;
  count: number;
  onChange: (id: string, patch: Partial<WorkflowFormField>) => void;
  onMove: (id: string, direction: "up" | "down") => void;
  onRemove: (id: string) => void;
  onReorder: (sourceId: string, targetId: string) => void;
  /** Validation issues on this field, shown under it. */
  issues?: string[];
}

const WorkflowFormFieldRow: React.FC<WorkflowFormFieldRowProps> = ({
  field,
  index,
  count,
  onChange,
  onMove,
  onRemove,
  onReorder,
  issues = [],
}) => {
  const viewOnly = useWorkflowReadOnly();
  const cardRef = useRef<HTMLDivElement>(null);
  const dragFlagTimer = useRef<number>();
  const [dropTarget, setDropTarget] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [blockedFileTypeAttempt, setBlockedFileTypeAttempt] = useState(false);
  const labelId = `field-${field.id}-label`;
  const typeLabelId = `field-${field.id}-type`;
  const visibilityLabelId = `field-${field.id}-visibility`;
  const hasSettings = Object.values(field.settings).some(
    (value) => value !== null && value !== "",
  );

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.id, { label: event.target.value });
  };

  const handleHelpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.id, { help: event.target.value });
  };

  const handleDataTypeChange = (event: SelectChangeEvent) => {
    const dataType = event.target.value as WorkflowFieldDataType;
    // Seed one blank row so the options editor isn't an empty box the moment it appears.
    const options =
      dataType === "dropdown" && field.options.length === 0 ? [""] : field.options;
    onChange(field.id, { dataType, options });
  };

  const handleVisibilityChange = (event: SelectChangeEvent) => {
    onChange(field.id, { visibility: event.target.value as WorkflowFieldVisibility });
  };

  const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.id, { required: event.target.checked });
  };

  const handleReadOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.id, { readOnly: event.target.checked });
  };

  const handleToggleSettings = () => setSettingsOpen((current) => !current);

  const handleNumericSettingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const setting = event.target.name as "min" | "max" | "rows" | "maxSize";
    const value = event.target.value === "" ? null : Number(event.target.value);
    onChange(field.id, {
      settings: {
        ...field.settings,
        [setting]: value,
      },
    });
  };

  const handleAllowedTypesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const entries = event.target.value.split(",");
    const isBlocked = (entry: string) =>
      BLOCKED_FILE_TYPES.has(entry.trim().replace(/^\./, "").toLowerCase());
    const includesBlockedType = entries.some(isBlocked);
    // Reject prohibited extensions but surface the rejection inline; silently filtering this value
    // would make the author think the workflow accepted a setting that can never execute.
    const acceptedEntries = includesBlockedType
      ? entries.filter((entry) => !isBlocked(entry)).join(",")
      : event.target.value;
    setBlockedFileTypeAttempt(includesBlockedType);
    onChange(field.id, {
      settings: {
        ...field.settings,
        accept: acceptedEntries,
      },
    });
  };

  const handleMoveUp = () => onMove(field.id, "up");
  const handleMoveDown = () => onMove(field.id, "down");
  const handleRemove = () => onRemove(field.id);

  const handleAddOption = () => {
    onChange(field.id, { options: [...field.options, ""] });
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const position = Number(event.target.dataset.optionIndex);
    onChange(field.id, {
      options: field.options.map((option, optionIndex) =>
        optionIndex === position ? event.target.value : option,
      ),
    });
  };

  const handleRemoveOption = (event: React.MouseEvent<HTMLButtonElement>) => {
    const position = Number(event.currentTarget.dataset.optionIndex);
    onChange(field.id, {
      options: field.options.filter((_option, optionIndex) => optionIndex !== position),
    });
  };

  // The grip carries `draggable`, not the card. The card covers its own inputs and selects, and a
  // draggable ancestor swallows the press that should be selecting text in them — while
  // `dragstart` fires at the drag source (the card) rather than the control pressed, so the card
  // cannot tell the two gestures apart. Toggling the attribute from a mousedown handler races the
  // browser, which resolves the drag source as the press is delivered. A dedicated handle has
  // neither problem, and matches the option rows below. Keyboard users reorder with Move up/down.
  const handleDragStart = (event: React.DragEvent<HTMLElement>) => {
    // Sidenav mounts react-dnd's HTML5Backend for the folder tree. Its window dragstart
    // listener calls preventDefault on any drag it does not own, which cancels this one.
    // stopPropagation keeps the field reorder out of that listener. The option grips
    // below already do this, which is why only these cards failed.
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(FIELD_DRAG_TYPE, field.id);
    if (cardRef.current) {
      event.dataTransfer.setDragImage(cardRef.current, 24, 24);
    }
    // The dimming class has to land *after* this tick. React flushes a `dragstart` state update
    // synchronously, so setting it here restyles the card — the very element just handed to
    // setDragImage — before the browser has snapshotted it, and Chromium answers a drag source
    // mutated mid-`dragstart` by cancelling the gesture outright, so the grip looks dead. The
    // option grips below change no state on dragstart, which is why only the field cards stuck.
    dragFlagTimer.current = window.setTimeout(() => setDragging(true));
  };

  const handleDragEnd = () => {
    window.clearTimeout(dragFlagTimer.current);
    setDragging(false);
    // A drag cancelled with Esc or dropped outside the list still has to clear this card's
    // highlight — no drop handler runs in either case.
    setDropTarget(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes(FIELD_DRAG_TYPE)) return;
    // Deliberately no preventDefault on the card being dragged: dropping it on itself is a no-op,
    // so the browser should show "no drop" rather than invite a drop that changes nothing.
    if (dragging) return;
    event.preventDefault();
    // The same backend sets dropEffect to "none" on every dragover it sees, so the browser
    // then refuses the drop. Stopping here leaves the move effect this card just set.
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    setDropTarget(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    // dragleave bubbles, so crossing into any of the card's own children reports as leaving the
    // card. Clearing on those would fight dragover, which sets it straight back — the highlight
    // flickers for the whole drag. Only a pointer that has actually left the card counts.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setDropTarget(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDropTarget(false);
    window.clearTimeout(dragFlagTimer.current);
    setDragging(false);
    const sourceId = event.dataTransfer.getData(FIELD_DRAG_TYPE);
    if (sourceId) onReorder(sourceId, field.id);
  };

  const handleOptionDragStart = (event: React.DragEvent<HTMLElement>) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      OPTION_DRAG_TYPE,
      `${field.id}|${event.currentTarget.dataset.optionIndex}`,
    );
  };

  const handleOptionDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes(OPTION_DRAG_TYPE)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
  };

  const handleOptionDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const [sourceFieldId, rawIndex] = event.dataTransfer.getData(OPTION_DRAG_TYPE).split("|");
    // Options belong to one field; dragging across cards would need a label merge we don't define.
    if (sourceFieldId !== field.id) return;
    const from = Number(rawIndex);
    const to = Number(event.currentTarget.dataset.optionIndex);
    if (Number.isNaN(from) || Number.isNaN(to) || from === to) return;
    const reordered = [...field.options];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    onChange(field.id, { options: reordered });
  };

  useEffect(() => () => window.clearTimeout(dragFlagTimer.current), []);

  return (
    <Box
      ref={cardRef}
      className={`workflow-canvas-dialog__field-card${
        dropTarget ? " workflow-canvas-dialog__field-card--drop-target" : ""
      }${dragging ? " workflow-canvas-dialog__field-card--dragging" : ""}${
        issues.length ? " workflow-canvas-dialog__field-card--invalid" : ""
      }`}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Box className="workflow-canvas-dialog__field-row">
        <Tooltip title="Drag to reorder" placement="top">
          <span
            className="workflow-canvas-dialog__grip"
            draggable={!viewOnly}
            onDragStart={handleDragStart}
            aria-label={`Reorder ${field.label || "field"} by dragging`}
          >
            <DragIndicator />
          </span>
        </Tooltip>
        <TextField
          disabled={viewOnly}
          id={labelId}
          label="Label"
          value={field.label}
          onChange={handleLabelChange}
          placeholder="Field label"
          size="small"
        />
        <FormControl disabled={viewOnly} size="small">
          <InputLabel id={typeLabelId}>Data type</InputLabel>
          <Select
            labelId={typeLabelId}
            label="Data type"
            value={field.dataType}
            onChange={handleDataTypeChange}
          >
            {WORKFLOW_FIELD_DATA_TYPES.map((dataType) => (
              <MenuItem key={dataType.value} value={dataType.value}>
                {dataType.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox disabled={viewOnly} checked={field.required} onChange={handleRequiredChange} />}
          label="Required"
        />
        <FormControlLabel
          control={<Checkbox disabled={viewOnly} checked={field.readOnly} onChange={handleReadOnlyChange} />}
          label="Read-only"
        />
        <FormControl disabled={viewOnly} size="small">
          <InputLabel id={visibilityLabelId}>Default visibility</InputLabel>
          <Select
            labelId={visibilityLabelId}
            label="Default visibility"
            value={field.visibility}
            onChange={handleVisibilityChange}
          >
            <MenuItem value="show">Show</MenuItem>
            <MenuItem value="hide">Hide</MenuItem>
          </Select>
        </FormControl>
        <Box className="workflow-canvas-dialog__field-tools">
          <Tooltip title={hasSettings ? "Field settings (set)" : "Field settings"} placement="top">
            <IconButton
              className={hasSettings ? "workflow-canvas-dialog__settings-toggle--set" : ""}
              aria-label={`Field settings for ${field.label || "field"}`}
              aria-expanded={settingsOpen}
              onClick={handleToggleSettings}
            >
              <LockOutlined />
            </IconButton>
          </Tooltip>
          {/* A disabled button fires no pointer events, so Tooltip needs a live wrapper to listen
              on — the move buttons disable at the ends of the list. */}
          <Tooltip title="Move up" placement="top">
            <span className="d-inline-flex">
              <IconButton
                aria-label={`Move ${field.label || "field"} up`}
                onClick={handleMoveUp}
                disabled={viewOnly || index === 0}
              >
                <ArrowUpward />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down" placement="top">
            <span className="d-inline-flex">
              <IconButton
                aria-label={`Move ${field.label || "field"} down`}
                onClick={handleMoveDown}
                disabled={viewOnly || index === count - 1}
              >
                <ArrowDownward />
              </IconButton>
            </span>
          </Tooltip>
          {!viewOnly && (
            <Tooltip title="Delete field" placement="top">
              <IconButton
                aria-label={`Delete ${field.label || "field"}`}
                onClick={handleRemove}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {settingsOpen && (
        <Box className="workflow-canvas-dialog__field-settings">
          <Typography className="workflow-canvas-dialog__field-settings-title">
            <LockOutlined />
            {WORKFLOW_FIELD_DATA_TYPES.find(({ value }) => value === field.dataType)?.label} settings
          </Typography>

          {(field.dataType === "text" || field.dataType === "richtext") && (
            <Box className="workflow-canvas-dialog__field-settings-grid">
              <TextField
                disabled={viewOnly}
                name="min"
                type="number"
                label="Min characters"
                value={field.settings.min ?? ""}
                onChange={handleNumericSettingChange}
                size="small"
                inputProps={{ min: 0 }}
              />
              <TextField
                disabled={viewOnly}
                name="max"
                type="number"
                label="Max characters"
                value={field.settings.max ?? ""}
                onChange={handleNumericSettingChange}
                size="small"
                inputProps={{ min: 0 }}
              />
              {field.dataType === "richtext" && (
                <TextField
                  disabled={viewOnly}
                  name="rows"
                  type="number"
                  label="Rows shown"
                  value={field.settings.rows ?? ""}
                  onChange={handleNumericSettingChange}
                  size="small"
                  inputProps={{ min: 1 }}
                />
              )}
            </Box>
          )}

          {field.dataType === "number" && (
            <Box className="workflow-canvas-dialog__field-settings-grid">
              <TextField
                disabled={viewOnly}
                name="min"
                type="number"
                label="Minimum"
                value={field.settings.min ?? ""}
                onChange={handleNumericSettingChange}
                size="small"
              />
              <TextField
                disabled={viewOnly}
                name="max"
                type="number"
                label="Maximum"
                value={field.settings.max ?? ""}
                onChange={handleNumericSettingChange}
                size="small"
              />
            </Box>
          )}

          {field.dataType === "file" && (
            <>
              <Box className="workflow-canvas-dialog__field-settings-grid">
                <TextField
                  disabled={viewOnly}
                  name="maxSize"
                  type="number"
                  label="Max file size (MB)"
                  value={field.settings.maxSize ?? ""}
                  onChange={handleNumericSettingChange}
                  size="small"
                  inputProps={{ min: 1 }}
                />
                <TextField
                  disabled={viewOnly}
                  label="Allowed file types"
                  value={field.settings.accept}
                  onChange={handleAllowedTypesChange}
                  placeholder="pdf, jpg, png, docx"
                  size="small"
                  error={blockedFileTypeAttempt}
                  helperText={
                    blockedFileTypeAttempt
                      ? "Executable types (exe, bat, ps1) cannot be allowed."
                      : "Separate extensions with commas."
                  }
                />
              </Box>
              <Typography className="workflow-canvas-dialog__blocked-types">
                Executable types (exe, bat, ps1) are always blocked by the system.
              </Typography>
            </>
          )}

          {/* Everything without a settings block above — dropdown, user picker, Boolean, date,
              date & time — so a new type never opens an empty popover. */}
          {!TYPES_WITH_SETTINGS.includes(field.dataType) && (
            <Typography className="workflow-canvas-dialog__no-field-settings">
              This field type has no additional settings.
            </Typography>
          )}
        </Box>
      )}

      <TextField
        disabled={viewOnly}
        label="Help (tooltip or URL)"
        value={field.help}
        onChange={handleHelpChange}
        placeholder="Short tooltip, or a https:// link"
        size="small"
        fullWidth
      />

      {issues.length > 0 && (
        <ul className="workflow-canvas-dialog__field-issues" aria-label={`Issues on ${field.label || "this field"}`}>
          {issues.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      {field.dataType === "dropdown" && (
        <Box className="workflow-canvas-dialog__options">
          <Typography className="workflow-canvas-dialog__options-title">
            Options — drag to reorder
          </Typography>
          {field.options.map((option, optionIndex) => (
            <Box
              className="workflow-canvas-dialog__option"
              key={`${field.id}-option-${optionIndex}`}
              data-option-index={optionIndex}
              onDragOver={handleOptionDragOver}
              onDrop={handleOptionDrop}
            >
              <Tooltip title="Drag to reorder" placement="top">
                <span
                  className="workflow-canvas-dialog__grip"
                  draggable={!viewOnly}
                  data-option-index={optionIndex}
                  onDragStart={handleOptionDragStart}
                  aria-label={`Reorder option ${optionIndex + 1} by dragging`}
                >
                  <DragIndicator />
                </span>
              </Tooltip>
              <TextField
                disabled={viewOnly}
                value={option}
                onChange={handleOptionChange}
                placeholder={`Option ${optionIndex + 1}`}
                size="small"
                fullWidth
                inputProps={{
                  "data-option-index": optionIndex,
                  "aria-label": `Option ${optionIndex + 1}`,
                }}
              />
              {!viewOnly && (
                <Tooltip title="Remove option" placement="top">
                  <IconButton
                    aria-label={`Remove option ${optionIndex + 1}`}
                    data-option-index={optionIndex}
                    onClick={handleRemoveOption}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          <Button
            disabled={viewOnly}
            className="workflow-canvas-dialog__mini-add"
            startIcon={<Add />}
            onClick={handleAddOption}
          >
            Add option
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default WorkflowFormFieldRow;
